import { useMemo } from 'react';

import { professionalInvoice } from '@pharmaplan/common';

import { IInvoiceStateBookings } from '../../components/Invoice/InvoiceLayout/InvoiceLayout';
import { getDateTimeDiff } from '../../components/Modals/WorkshiftDetails/utils';

import { useAppSelector } from '../useAppSelector';

const useInvoicesCalculation = (
  invoiceState: Record<string, IInvoiceStateBookings>,
) => {
  const invoice = useAppSelector(professionalInvoice);
  const { tps, tvq, taxOnTax } = invoice ?? {};

  const totals = useMemo(
    () =>
      (Object.values(invoiceState))?.reduce(
        (
          acc: { totalHours: number; totalRate: number },
          curr: IInvoiceStateBookings,
        ) => {
          const { startDate, endDate } = invoiceState?.[curr.bookingId] ?? {};
          const diff = getDateTimeDiff(startDate, endDate);
          acc.totalHours += diff ?? 0;
          acc.totalRate += (diff ?? 0) * curr.paidHourlyRate;
          return acc;
        },
        { totalHours: 0, totalRate: 0 },
      ),
    [invoiceState],
  );

  const totalAllowancesCalculation = useMemo(
    () =>
      invoice.bookings.reduce(
        (acc, curr) => {
          const {
            travelAllowance = '0',
            mealAllowance,
            accommodationAllowance,
            emergencyFees,
            otherAllowance,
          } = curr ?? {};

          acc.travel += parseFloat(travelAllowance ?? '0');
          acc.meal += parseFloat(mealAllowance ?? '0');
          acc.accommodation += parseFloat(accommodationAllowance ?? '0');
          acc.emergency += parseFloat(emergencyFees ?? '0');
          acc.other += parseFloat(otherAllowance ?? '0');

          return acc;
        },
        { travel: 0, meal: 0, accommodation: 0, emergency: 0, other: 0 },
      ),
    [invoice.bookings],
  );

  const subTotal = totals.totalRate
    + totalAllowancesCalculation.accommodation
    + totalAllowancesCalculation.emergency
    + totalAllowancesCalculation.meal
    + totalAllowancesCalculation.travel + totalAllowancesCalculation.other;

  const totalTPS = (parseFloat(tps ?? '0') / 100) * subTotal;
  let totalTVQ = 0;
  if (taxOnTax) {
    totalTVQ = (parseFloat(tvq ?? '0') / 100) * (subTotal + totalTPS);
  } else {
    totalTVQ = (parseFloat(tvq ?? '0') / 100) * subTotal;
  }

  const grandTotal = subTotal + totalTPS + totalTVQ;

  return {
    ...totalAllowancesCalculation,
    totalTPS,
    totalTVQ,
    ...totals,
    subTotal,
    grandTotal,
    tps,
    tvq,
  };
};

export default useInvoicesCalculation;
